import LogoHM from '@hconnect/common/branding/branding/icons/Logo-HM.svg'
import {useFeaturesCheckFetcher} from '@hconnect/common/components/FeaturesCheck'
import {trackEventWithBrowserProps} from '@hconnect/common/logging/Analytics'
import {CommonHeader, NavItem, useBreakPoints, useElementSize} from '@hconnect/uikit'
import {Box} from '@mui/material'
import {nanoid} from 'nanoid'
import {SnackbarProvider} from 'notistack'
import React, {useEffect, useRef} from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {useSelector} from 'react-redux'
import {BrowserRouter as Router, Redirect, Route, Switch, useLocation} from 'react-router-dom'

import {api, loginFlow} from './App.store'
import {useStyles} from './App.style'
import {isProd} from './common/Constants'
import {useAdminPilotUser} from './common/hooks/useAdminPilotUser'
import {useUserRoles} from './common/hooks/useUserRoles'
import {getCountryFullName} from './common/utils/utils'
import {AutoLogout} from './components/AutoLogout/AutoLogout'
import {autoLogoutConfig} from './components/AutoLogout/AutoLogoutConfig'
import {HeaderLogo} from './components/HeaderLogo'
import {MHCUserMenu} from './components/MHCUserMenu'
import {UserCreationRolesProvider} from './contexts/UserCreationRolesContext'
import {ErrorInfo} from './ErrorInfo'
import {useHeaderBrandings} from './hooks/useHeaderBrandings'
import {useTranslation} from './hooks/useTranslation'
import {
  selectLoggedInUserPermissions,
  selectLoggedInUserProfile
} from './modules/LoggedInUser.selectors'
import {
  CountryConfigurationsEntity,
  CountryConfigurationsListofEntities
} from './pages/Configurations/CountryConfigurations'
import {CatalogConfigurationsList} from './pages/Configurations/CatalogConfigurations/CatalogConfigurationsList'
import {CatalogConfigurationEditor} from './pages/Configurations/CatalogConfigurations/CatalogConfigurationEditor'
import {CatalogConfigurationCreate} from './pages/Configurations/CatalogConfigurations/CatalogConfigurationCreate'
import {GlobalConfigurationEditor} from './pages/Configurations/CatalogConfigurations/GlobalConfigurationEditor'
import {AuditLogList} from './pages/Configurations/CatalogConfigurations/AuditLogList'
import {useCanEditGlobalCatalog} from './pages/Configurations/CatalogConfigurations/hooks/useCanEditGlobalCatalog'
import {BUSINESS_LINE_SHIPPING_TYPES, COUNTRY_CONFIGURATION_BUSINESS_LINES} from './pages/Configurations/CountryConfigurations/constants'
import {Announcements} from './pages/Announcements/Announcements'
import {AnnouncementCreate} from './pages/Announcements/Announcements.create'
import {CompanyList} from './pages/CompanyList/CompanyList'
import {Whitelisting} from './pages/Configurations/Whitelisting'
import {CustomerTierSettingsCopy} from './pages/Configurations/HConnect/CustomerTierSettings/CustomerTierSettingsCopy'
import {CustomerTierSettingsCreate} from './pages/Configurations/HConnect/CustomerTierSettings/CustomerTierSettingsCreate'
import {CustomerTierSettingsEdit} from './pages/Configurations/HConnect/CustomerTierSettings/CustomerTierSettingsEdit'
import {CustomerTierSettingsList} from './pages/Configurations/HConnect/CustomerTierSettings/CustomerTierSettingsList'
import {HConnectConfigurationsLanding} from './pages/Configurations/HConnect/HConnectConfigurationsLanding'
import {useAllowedHConnectConfigCountries} from './pages/Configurations/HConnect/hooks/useAllowedHConnectConfigCountries'
import {OrderCancellationConfigurationEdit} from './pages/Configurations/HConnect/OrderCancellation/OrderCancellationConfigurationEdit'
import {OrderCancellation} from './pages/Configurations/HConnect/OrderCancellation/OrderCancellationConfigurationList'
import {OrderChangeConfigurationCopy} from './pages/Configurations/HConnect/OrderManagement/OrderChangeConfigurationCopy'
import {OrderChangeConfigurationCreate} from './pages/Configurations/HConnect/OrderManagement/OrderChangeConfigurationCreate'
import {OrderChangeConfigurationEdit} from './pages/Configurations/HConnect/OrderManagement/OrderChangeConfigurationEdit'
import {OrderChangeConfigurationsList} from './pages/Configurations/HConnect/OrderManagement/OrderChangeConfigurationsList'
import {SlotManagementConfigurationCopy} from './pages/Configurations/HConnect/SlotManagement/SlotManagementConfigurationCopy'
import {SlotManagementConfigurationCreate} from './pages/Configurations/HConnect/SlotManagement/SlotManagementConfigurationCreate'
import {SlotManagementConfigurationEdit} from './pages/Configurations/HConnect/SlotManagement/SlotManagementConfigurationEdit'
import {SlotManagementConfigurationList} from './pages/Configurations/HConnect/SlotManagement/SlotManagementConfigurationList'
import {CreateCompany} from './pages/CreateCompany/CreateCompany'
import {CreateUser} from './pages/CreateUser/CreateUser'
import {FeatureCreate} from './pages/FeaturesList/components/FeatureCreate'
import {FeaturesList} from './pages/FeaturesList/FeaturesList'
import {LegalDocuments} from './pages/LegalDocuments/LegalDocuments'
import {ManageCompany} from './pages/ManageCompany/ManageCompany'
import {ManageUser} from './pages/ManageUser/ManageUser'
import {ManageUserPilot} from './pages/ManageUser/ManageUserPilot'
import {Audiences} from './pages/Messages/Audiences/Audiences'
import {CreateAudience} from './pages/Messages/Audiences/CreateAudience'
import {CreateMessage} from './pages/Messages/CreateMessage/CreateMessage'
import {MessagesList} from './pages/Messages/MessagesList/MessagesList'
import {QuickPreview} from './pages/QuickPreview/QuickPreview'
import {UsersList} from './pages/UsersList/UsersList'
import {Version} from './pages/Version'
import {WidgetUserCreation} from './pages/WidgetUserCreation/WidgetUserCreation'
import {WidgetUserList} from './pages/WidgetUserList/WidgetUserList'
import {PrivateRoute} from './PrivateRoute'

// eslint-disable-next-line complexity
export const App: React.FC = () => {
  const {t, i18n} = useTranslation()

  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const sessionKeyRef = useRef<null | string>(null)
  const location = useLocation()

  const {data: features} = useFeaturesCheckFetcher(api)

  const loggedInUserProfile = useSelector(selectLoggedInUserProfile)
  const permissions = useSelector(selectLoggedInUserPermissions)

  const {data: roles} = useUserRoles(loggedInUserProfile?.user_id)

  const {branding} = useHeaderBrandings(loggedInUserProfile?.defaultBranding)
  const formattedBranding = branding?.nationalLogoUrl?.includes('Logo-HM-Compact-Negative')
    ? LogoHM
    : branding?.nationalLogoUrl
  const canEditAnnouncements = Boolean(
    features &&
      features.find((feature) => feature.name === 'Announcements' && feature.enabled) &&
      loggedInUserProfile?.hasRoles.some((role) => ['GLOBAL_ADMIN', 'SUPER_ADMIN'].includes(role))
  )

  useEffect(() => {
    const defaultLocale = loggedInUserProfile?.defaultLocale
    if (defaultLocale) void i18n.changeLanguage(defaultLocale)
  }, [i18n, loggedInUserProfile])

  useEffect(() => {
    trackEventWithBrowserProps('adminPageview', {
      product: 'adminconsole',
      date: new Date().toISOString(),
      country: loggedInUserProfile?.country ?? '',
      role: loggedInUserProfile?.hasRoles,
      sessionKey: sessionKeyRef.current
    })
  }, [loggedInUserProfile, location])

  useEffect(() => {
    sessionKeyRef.current = nanoid()
  }, [loggedInUserProfile?.id])

  const canSeeWhitelisting =
    permissions.some((permission) => permission.permissionType === 'CHANGE_CONFIGURATIONS') &&
    !isProd

  const configurableHConnectCountries = useAllowedHConnectConfigCountries()
  const isCountryConfigurationsEnabled = Boolean(
    features?.find((feature) => feature.name === 'CountryConfigurations' && feature.enabled)
  )
  const canSeeHConnectConfigs = configurableHConnectCountries.length > 0
  const canSeeCountryConfigurations = isCountryConfigurationsEnabled && canSeeHConnectConfigs
  // Global metadata is cross-country -> require a global admin role, not just per-country config access.
  const hasGlobalCatalogRole = useCanEditGlobalCatalog()
  const canEditGlobalCatalog = canSeeCountryConfigurations && hasGlobalCatalogRole

  const canOnlySeeUserAdminPilot = useAdminPilotUser()

  const canSeeFeatures = permissions.some(
    (permission) => permission.permissionType === 'VIEW_FEATURES'
  )

  const canEditFeatures =
    permissions.some((permission) => permission.permissionType === 'CHANGE_FEATURES') ||
    !!roles?.find(
      (role) =>
        role.roleType === 'GLOBAL_ADMIN' &&
        (role.dataScope?.['countryId'] === '*' || role.dataScope?.['areaId'] === '*')
    ) ||
    false

  const canSeeMessages = permissions.some(
    (permission) => permission.permissionType === 'MANAGE_MESSAGES'
  )

  const canManageCompanies = permissions.some(
    (permission) => permission.permissionType === 'MANAGE_COMPANIES'
  )

  const routes: NavItem[] = [
    {
      label: t('navigation.users'),
      url: '/users',
      dataTestId: 'nav-link-users'
    }
  ]

  const getBusinessLineItems = (country: string) =>
    COUNTRY_CONFIGURATION_BUSINESS_LINES.map(({label, slug, dataTestId}) => {
      const baseUrl = `/country-configurations/${country}/${slug}`
      const shippingTypes = BUSINESS_LINE_SHIPPING_TYPES[slug]

      const defaultUrl = shippingTypes && shippingTypes.length > 0
        ? `${baseUrl}?shippingType=${shippingTypes[0].value}`
        : baseUrl

      const item: NavItem = {
        label,
        url: defaultUrl,
        dataTestId
      }

      if (shippingTypes && shippingTypes.length > 0) {
        item.subItems = shippingTypes.map((pt) => ({
          label: t(pt.labelKey),
          url: `${baseUrl}?shippingType=${pt.value}`,
          dataTestId: pt.dataTestId,
          isActive: (_match, location) => {
            if (location.pathname !== baseUrl) return false
            const params = new URLSearchParams(location.search)
            return params.get('shippingType') === pt.value
          }
        }))
      }

      return item
    })

  if (canManageCompanies) {
    routes.push({
      label: t('navigation.companies'),
      url: '/companies',
      dataTestId: 'nav-link-companies'
    })
  }
  if (canSeeWhitelisting && !canSeeHConnectConfigs) {
    routes.push({
      label: t('navigation.configurations.whitelisting'),
      url: '/configuration',
      dataTestId: 'nav-link-configurations'
    })
  }

  if (canSeeWhitelisting && (canSeeHConnectConfigs || canSeeCountryConfigurations)) {
    const transactionalUseCase =
      configurableHConnectCountries.length > 1
        ? {
            label: t('navigation.configurations.transactionalUseCase'),
            url: `/country-configurations/${configurableHConnectCountries[0]}/${COUNTRY_CONFIGURATION_BUSINESS_LINES[0].slug}`,
            dataTestId: 'nav-link-transactional-use-case',
            subItems: configurableHConnectCountries.map((country) => ({
              label: getCountryFullName(country),
              url: `/country-configurations/${country}/${COUNTRY_CONFIGURATION_BUSINESS_LINES[0].slug}`,
              dataTestId: `nav-link-country-configurations-${country.toLowerCase()}`,
              subItems: getBusinessLineItems(country)
            }))
          }
        : {
            label: t('navigation.configurations.transactionalUseCase'),
            url: `/country-configurations/${configurableHConnectCountries[0]}/${COUNTRY_CONFIGURATION_BUSINESS_LINES[0].slug}`,
            dataTestId: 'nav-link-transactional-use-case',
            subItems: getBusinessLineItems(configurableHConnectCountries[0])
          }

    routes.push({
      label: t('navigation.configurations.configurations'),
      url: '/configuration',
      dataTestId: 'nav-link-configurations-nested',
      subItems: [
        {
          label: t('navigation.configurations.whitelisting'),
          url: '/configuration',
          dataTestId: 'nav-link-configurations'
        },
        {
          label: t('navigation.configurations.hconnect'),
          url: '/configuration/hconnect',
          dataTestId: 'nav-link-configurations-hconnect-nested',
          subItems: [
            {
              label: t('navigation.configurations.orderManagement'),
              url: '/configuration/hconnect/order-management',
              dataTestId: 'nav-link-configurations-hconnect-order-change'
            },
            {
              label: t('navigation.configurations.slotManagement'),
              url: '/configuration/hconnect/slot-management',
              dataTestId: 'nav-link-configurations-hconnect-slot-management'
            },
            {
              label: t('navigation.configurations.orderCancellation'),
              url: '/configuration/hconnect/order-cancellation',
              dataTestId: 'nav-link-configurations-hconnect-order-cancellation'
            },
            {
              label: t('navigation.configurations.customerTierSettings'),
              url: '/configuration/hconnect/customer-tier-settings',
              dataTestId: 'nav-link-configurations-hconnect-customer-tier-settings'
            }
          ]
        },
        ...(canSeeCountryConfigurations ? [transactionalUseCase] : [])
      ]
    })
  }

  if (!canSeeWhitelisting && (canSeeHConnectConfigs || canSeeCountryConfigurations)) {
    const hconnectSubItem = {
      label: t('navigation.configurations.hconnect'),
      url: '/configuration/hconnect',
      dataTestId: 'nav-link-configurations-hconnect-nested',
      subItems: [
        {
          label: t('navigation.configurations.orderManagement'),
          url: '/configuration/hconnect/order-management',
          dataTestId: 'nav-link-configurations-hconnect-order-change'
        },
        {
          label: t('navigation.configurations.slotManagement'),
          url: '/configuration/hconnect/slot-management',
          dataTestId: 'nav-link-configurations-hconnect-slot-management'
        },
        {
          label: t('navigation.configurations.orderCancellation'),
          url: '/configuration/hconnect/order-cancellation',
          dataTestId: 'nav-link-configurations-hconnect-order-cancellation'
        },
        {
          label: t('navigation.configurations.customerTierSettings'),
          url: '/configuration/hconnect/customer-tier-settings',
          dataTestId: 'nav-link-configurations-hconnect-customer-tier-settings'
        }
      ]
    }

    const countryConfigSubItems = canSeeCountryConfigurations
      ? configurableHConnectCountries.length > 1
        ? configurableHConnectCountries.map((country) => ({
            label: getCountryFullName(country),
            url: `/country-configurations/${country}/${COUNTRY_CONFIGURATION_BUSINESS_LINES[0].slug}`,
            dataTestId: `nav-link-country-configurations-${country.toLowerCase()}`,
            subItems: getBusinessLineItems(country)
          }))
        : getBusinessLineItems(configurableHConnectCountries[0])
      : []

    routes.push({
      label: t('navigation.configurations.configurations'),
       url: canSeeCountryConfigurations
        ? `/country-configurations/${configurableHConnectCountries[0]}/${COUNTRY_CONFIGURATION_BUSINESS_LINES[0].slug}`
        : '/configuration/hconnect',
      dataTestId: 'nav-link-configurations-nested',
      subItems: [hconnectSubItem, ...countryConfigSubItems]
    })
  }

  if (canSeeCountryConfigurations) {
    routes.push({
      label: 'Catalog Configurations',
      url: '/catalog-configurations',
      dataTestId: 'nav-link-catalog-configurations',
      subItems: [
        {
          label: 'Configurations',
          url: '/catalog-configurations',
          dataTestId: 'nav-link-catalog-configurations-list'
        },
        {
          label: 'Audit log',
          url: '/catalog-configurations/audit-log',
          dataTestId: 'nav-link-catalog-configurations-audit-log'
        }
      ]
    })
  }

  if (canEditAnnouncements) {
    routes.push({
      label: t('navigation.announcements'),
      url: '/announcements',
      dataTestId: 'nav-link-announcements'
    })
  }

  if (canSeeFeatures) {
    routes.push({
      label: t('navigation.features'),
      url: '/features',
      dataTestId: 'nav-link-features'
    })
  }

  if (canSeeMessages) {
    routes.push({
      label: t('navigation.messages.main'),
      url: '/messages',
      dataTestId: 'nav-link-messages',
      subItems: [
        {label: t('navigation.audiences'), url: '/audiences', dataTestId: 'nav-link-audiences'},
        {label: t('navigation.messages.subItem'), url: '/messages', dataTestId: 'nav-link-messages'}
      ]
    })
  }

  const [ref, {width}] = useElementSize()
  const {classes} = useStyles()

  return (
    <Router>
      <ErrorBoundary FallbackComponent={() => <ErrorInfo />}>
        <Switch>
          <Route
            path="/manage/quick-preview/:userId"
            exact
            render={() => (
              <Box className={classes.frameStyle}>
                <SnackbarProvider
                  classes={{
                    containerRoot: classes.root
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                >
                  <Box className={classes.headerLogoContainer}>
                    <HeaderLogo
                      logo={formattedBranding}
                      isCustomerAdmin={true}
                      isQuickPreview={true}
                    />
                  </Box>
                  <QuickPreview />
                </SnackbarProvider>
              </Box>
            )}
          />
          <Route
            path="/widgetUserList"
            exact
            render={() => (
              <Box className={classes.frameStyle}>
                <SnackbarProvider
                  classes={{
                    containerRoot: classes.root
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                >
                  <Box className={classes.headerLogoContainer}>
                    <HeaderLogo
                      logo={formattedBranding}
                      isCustomerAdmin={true}
                      isQuickPreview={true}
                    />
                  </Box>
                  <WidgetUserList />
                </SnackbarProvider>
              </Box>
            )}
          />
          <Route
            path="/widgetUserCreation"
            exact
            render={() => (
              <Box className={classes.frameStyle}>
                <SnackbarProvider
                  classes={{
                    containerRoot: classes.root
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                >
                  <Box className={classes.headerLogoContainer}>
                    <HeaderLogo
                      logo={branding?.nationalLogoUrl}
                      isCustomerAdmin={true}
                      isQuickPreview={true}
                    />
                  </Box>
                  <WidgetUserCreation />
                </SnackbarProvider>
              </Box>
            )}
          />
          <Route
            path="/"
            render={() => (
              <SnackbarProvider
                classes={{
                  containerRoot: classes.root
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
              >
                <div className="flex flex-col min-h-screen">
                  <div ref={ref} className="flex justify-between items-center px-12 ">
                    {!isMobile && (
                      <HeaderLogo
                        logo={formattedBranding}
                        isCustomerAdmin={canOnlySeeUserAdminPilot}
                      />
                    )}
                    <div className="w-auto lg:w-full relative z-[9999]">
                      <div className={classes.mhcHeader}>
                        <CommonHeader
                          appName="HConnect"
                          navItems={routes}
                          disableAppSelect
                          parentWidth={width}
                          keepPointerEvents={true}
                          navItemClassNames={`${classes.activeNavItemClassName} ${classes.navItemClassName}`}
                        />
                      </div>
                    </div>
                    {isMobile && (
                      <HeaderLogo
                        withoutBranding={true}
                        isCustomerAdmin={canOnlySeeUserAdminPilot}
                      />
                    )}
                    <MHCUserMenu />
                  </div>
                  <Route path="/users" exact render={() => <UsersList />} />
                  <Route path="/useradmin" exact render={() => <UsersList />} />
                  <Route
                    path="/createUser"
                    exact
                    render={() => (
                      <UserCreationRolesProvider>
                        <CreateUser />
                      </UserCreationRolesProvider>
                    )}
                  />
                  <Route
                    path="/manage/:userId"
                    exact
                    render={() => (
                      <UserCreationRolesProvider>
                        <ManageUser />
                      </UserCreationRolesProvider>
                    )}
                  />
                  <Route
                    path="/managePilot/:userId"
                    exact
                    render={() => (
                      <UserCreationRolesProvider>
                        <ManageUser />
                      </UserCreationRolesProvider>
                    )}
                  />
                  <Route path="/companies" exact render={() => <CompanyList />} />
                  <Route path="/companies/create" exact render={() => <CreateCompany />} />
                  <Route
                    path="/companies/manage/:companyId"
                    exact
                    render={() => <ManageCompany />}
                  />
                  <PrivateRoute
                    path="/announcements"
                    exact
                    allowed={canEditAnnouncements}
                    render={() => <Announcements />}
                  />
                  <PrivateRoute
                    allowed={canEditAnnouncements}
                    path="/announcements/create"
                    exact
                    render={() => <AnnouncementCreate />}
                  />
                  <PrivateRoute
                    allowed={canEditAnnouncements}
                    path="/announcements/manage/:messageId"
                    exact
                    render={() => <AnnouncementCreate />}
                  />
                  <PrivateRoute
                    path="/configuration"
                    exact
                    allowed={canSeeWhitelisting}
                    render={() => <Whitelisting />}
                  />
                  <PrivateRoute
                    path="/country-configurations/:countryId/:businessLine"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <CountryConfigurationsListofEntities />}
                  />
                  <PrivateRoute
                    path="/country-configurations/:countryId/:businessLine/:entityType"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <CountryConfigurationsEntity />}
                  />
                  <PrivateRoute
                    path="/catalog-configurations"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <CatalogConfigurationsList />}
                  />
                  <PrivateRoute
                    path="/catalog-configurations/new"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <CatalogConfigurationCreate />}
                  />
                  <PrivateRoute
                    path="/catalog-configurations/global"
                    exact
                    allowed={canEditGlobalCatalog}
                    render={() => <GlobalConfigurationEditor />}
                  />
                  <PrivateRoute
                    path="/catalog-configurations/audit-log"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <AuditLogList />}
                  />
                  <PrivateRoute
                    path="/catalog-configurations/:countryId/:businessLine"
                    exact
                    allowed={canSeeCountryConfigurations}
                    render={() => <CatalogConfigurationEditor />}
                  />
                  <PrivateRoute
                    path="/configuration/hconnect"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <HConnectConfigurationsLanding />}
                  />
                  <PrivateRoute
                    path="/configuration/hconnect/order-management/"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <OrderChangeConfigurationsList />}
                  />
                  <Switch>
                    <PrivateRoute
                      path="/configuration/hconnect/order-management/new"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <OrderChangeConfigurationCreate />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/order-management/:configId/edit"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <OrderChangeConfigurationEdit />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/order-management/:configId/copy"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <OrderChangeConfigurationCopy />}
                    />
                  </Switch>
                  <PrivateRoute
                    path="/configuration/hconnect/slot-management/"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <SlotManagementConfigurationList />}
                  />
                  <Switch>
                    <PrivateRoute
                      path="/configuration/hconnect/slot-management/new"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <SlotManagementConfigurationCreate />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/slot-management/:configId/edit"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <SlotManagementConfigurationEdit />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/slot-management/:configId/copy"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <SlotManagementConfigurationCopy />}
                    />
                  </Switch>
                  <PrivateRoute
                    path="/configuration/hconnect/order-cancellation"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <OrderCancellation />}
                  />
                  <Switch>
                    <PrivateRoute
                      path="/configuration/hconnect/order-cancellation/:configId/edit"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <OrderCancellationConfigurationEdit />}
                    />
                  </Switch>
                  <PrivateRoute
                    path="/configuration/hconnect/order-cancellation/:configId/copy"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <OrderChangeConfigurationCopy />}
                  />
                  <PrivateRoute
                    path="/configuration/hconnect/customer-tier-settings/"
                    exact
                    allowed={canSeeHConnectConfigs}
                    render={() => <CustomerTierSettingsList />}
                  />
                  <Switch>
                    <PrivateRoute
                      path="/configuration/hconnect/customer-tier-settings/new"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <CustomerTierSettingsCreate />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/customer-tier-settings/:tierSettingsId/edit"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <CustomerTierSettingsEdit />}
                    />
                    <PrivateRoute
                      path="/configuration/hconnect/customer-tier-settings/:tierSettingsId/copy"
                      exact
                      allowed={canSeeHConnectConfigs}
                      render={() => <CustomerTierSettingsCopy />}
                    />
                  </Switch>
                  <PrivateRoute
                    path="/features"
                    exact
                    allowed={canSeeFeatures}
                    render={() => <FeaturesList />}
                  />
                  <PrivateRoute
                    allowed={canEditFeatures}
                    path="/features/create"
                    exact
                    render={() => <FeatureCreate />}
                  />
                  <PrivateRoute
                    allowed={canEditFeatures}
                    path="/features/edit/:featureName"
                    exact
                    render={() => <FeatureCreate />}
                  />
                  <PrivateRoute
                    path="/audiences"
                    exact
                    allowed={canSeeMessages}
                    render={() => <Audiences />}
                  />
                  <PrivateRoute
                    path="/createAudience"
                    exact
                    allowed={canSeeMessages}
                    render={() => <CreateAudience />}
                  />
                  <PrivateRoute
                    path="/messages/create"
                    exact
                    allowed={canSeeMessages}
                    render={() => <CreateMessage />}
                  />
                  <PrivateRoute
                    path="/messages/details/:messageId"
                    exact
                    allowed={canSeeMessages}
                    render={() => <CreateMessage isReadonly={true} />}
                  />
                  <PrivateRoute
                    path="/messages"
                    exact
                    allowed={canSeeMessages}
                    render={() => <MessagesList />}
                  />
                  <Route path="/legal" exact render={() => <LegalDocuments />} />
                  <Route path="/version" exact render={() => <Version />} />
                  <Route path="/" exact render={() => <Redirect to="/users" />} />
                </div>
              </SnackbarProvider>
            )}
          />
        </Switch>
      </ErrorBoundary>
      {loggedInUserProfile?.hasRoles.some((role) => autoLogoutConfig.roles.includes(role)) && (
        <AutoLogout
          onLogout={() => {
            void loginFlow.startLogoutProcess()
          }}
        />
      )}
    </Router>
  )
}
